import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Card, Col, Collapse, DatePicker, Form, Input, InputNumber, Row, Select, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { ApplicationDetailService, OutcomeUpdate, ServiceFormValues } from '../types/outcomeTypes';
import { isCopayService, getServiceType } from '../utils/serviceTypeMapper';

const { Text } = Typography;

const OUTCOME_OPTIONS = [
    'Approved', 'Pending', 'Denied', 'Completed',
    'Discarded', 'Withdrawn', 'Covered', 'NotCovered', 'Archived',
];

const CATEGORY_BADGE: Record<string, { color: string; label: string }> = {
    'CoPay':                { color: 'blue',   label: 'CP' },
    'Admin_Reimb_Support':  { color: 'orange', label: 'BI' },
    'Other_Education':      { color: 'purple', label: 'OE' },
    'Other_Limited_Supply': { color: 'green',  label: 'OLS' },
    'Free_Drug':            { color: 'cyan',   label: 'FD' },
    'Admin_Referral_To':    { color: 'magenta', label: 'RT' },
};

// Badge lookup is case-insensitive to tolerate AP API variation
function getBadge(desc: string) {
    const key = Object.keys(CATEGORY_BADGE).find(
        k => k.toLowerCase() === desc?.toLowerCase()
    );
    return key ? CATEGORY_BADGE[key] : undefined;
}

export interface ServiceCardHandle {
    getValues: () => ServiceFormValues | null;
}

interface Props {
    service: ApplicationDetailService;
    submitResult?: OutcomeUpdate;
    disabled?: boolean;
    onReadyChange?: (ready: boolean) => void;
}

/** Convert a form field value (dayjs OR string) to 'YYYY-MM-DD', or undefined. */
const toDateStr = (v: any): string | undefined => {
    if (!v) return undefined;
    if (typeof v === 'string') return v;
    return v?.format?.('YYYY-MM-DD');
};

const ServiceCard = forwardRef<ServiceCardHandle, Props>(({ service, submitResult, disabled, onReadyChange }, ref) => {
    const [form] = Form.useForm();
    const isOptionalOpen = isCopayService(service.serviceCategoryDescription);

    // Controlled open state — initialised based on service type so it's reliable
    // regardless of when/how the Collapse first renders.
    const [activeKeys, setActiveKeys] = useState<string[]>(isOptionalOpen ? ['optional'] : []);

    const badge = getBadge(service.serviceCategoryDescription);
    const isUnsupported = getServiceType(service.serviceCategoryDescription) === undefined;

    // Watch required fields to derive readiness and notify parent
    const outcome = Form.useWatch('outcome', form);
    const outcomeDate = Form.useWatch('outcomeDate', form);
    const isReady = !isUnsupported && !!(outcome && outcomeDate);

    useEffect(() => {
        onReadyChange?.(isReady);
    }, [isReady, onReadyChange]);

    // Prepopulate fields with any existing outcome data the API already returned.
    useEffect(() => {
        const vals: Record<string, any> = {};
        if (service.outcome)             vals.outcome           = service.outcome;
        if (service.outcomeDate)         vals.outcomeDate       = dayjs(service.outcomeDate);
        if (service.effectiveDateStart)  vals.effectiveDateStart = dayjs(service.effectiveDateStart);
        if (service.effectiveDateEnd)    vals.effectiveDateEnd  = dayjs(service.effectiveDateEnd);
        if (service.memberId)            vals.memberId          = service.memberId;
        if (service.awardAmount != null) vals.awardAmount       = service.awardAmount;
        if (service.rxBin)               vals.rxBin             = service.rxBin;
        if (service.rxPcn)               vals.rxPcn             = service.rxPcn;
        if (service.rxGroup)             vals.rxGroup           = service.rxGroup;
        if (Object.keys(vals).length > 0) form.setFieldsValue(vals);
    }, [service.applicationServiceId]); // intentional: only re-run when the service changes

    useImperativeHandle(ref, () => ({
        getValues: () => {
            const values = form.getFieldsValue();
            if (!values.outcome || !values.outcomeDate) return null;
            return {
                outcome:            values.outcome,
                outcomeDate:        toDateStr(values.outcomeDate) ?? '',
                effectiveDateStart: toDateStr(values.effectiveDateStart),
                effectiveDateEnd:   toDateStr(values.effectiveDateEnd),
                memberId:           values.memberId   || undefined,
                awardAmount:        values.awardAmount != null ? String(values.awardAmount) : undefined,
                rxBin:              values.rxBin   || undefined,
                rxPcn:              values.rxPcn   || undefined,
                rxGroup:            values.rxGroup || undefined,
            };
        },
    }));

    const statusTag = submitResult ? (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Tag color="default">{submitResult.originalStatus}</Tag>
            <Text type="secondary">→</Text>
            <Tag color={submitResult.newStatus === 'Approved' ? 'success' : submitResult.newStatus === 'Denied' ? 'error' : 'processing'}>
                {submitResult.newStatus}
            </Tag>
        </span>
    ) : null;

    const rightSection = submitResult
        ? statusTag
        : isUnsupported
            ? <Tag color="warning" style={{ fontSize: 11 }}>Unsupported service type</Tag>
            : disabled
                ? null
                : isReady
                    ? <Tag color="success" style={{ fontSize: 11 }}>Ready</Tag>
                    : <Tag color="default" style={{ fontSize: 11, color: '#8c8c8c' }}>Not Ready</Tag>;

    const cardTitle = (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {badge && <Tag color={badge.color} style={{ fontFamily: 'monospace', fontSize: 10 }}>{badge.label}</Tag>}
            {service.assistanceServiceName}
            <Text type="secondary" style={{ fontSize: 11, fontWeight: 400 }}>#{service.applicationServiceId}</Text>
            {rightSection && <span style={{ marginLeft: 'auto' }}>{rightSection}</span>}
        </span>
    );

    const optionalFields = (
        <Row gutter={[12, 0]}>
            {/* Date range */}
            <Col span={12}>
                <Form.Item label="Effective Start" name="effectiveDateStart">
                    <DatePicker style={{ width: '100%' }} disabled={disabled || isUnsupported} />
                </Form.Item>
            </Col>
            <Col span={12}>
                <Form.Item label="Effective End" name="effectiveDateEnd">
                    <DatePicker style={{ width: '100%' }} disabled={disabled || isUnsupported} />
                </Form.Item>
            </Col>

            {/* Member info */}
            <Col span={12}>
                <Form.Item label="Member ID" name="memberId" rules={[{ max: 50, message: 'Max 50 characters' }]}>
                    <Input placeholder="optional" disabled={disabled || isUnsupported} style={{ height: 32 }} />
                </Form.Item>
            </Col>
            <Col span={12}>
                <Form.Item label="Award Amount ($)" name="awardAmount">
                    <InputNumber
                        controls={false}
                        inputMode="decimal"
                        style={{ width: '100%' }}
                        placeholder="optional"
                        step={0.01}
                        min={0}
                        disabled={disabled || isUnsupported}
                    />
                </Form.Item>
            </Col>

            {/* RX trio */}
            <Col span={8}>
                <Form.Item label="RX BIN" name="rxBin" rules={[{ max: 6, message: 'Max 6 characters' }]}>
                    <Input placeholder="optional" disabled={disabled || isUnsupported} style={{ height: 32 }} />
                </Form.Item>
            </Col>
            <Col span={8}>
                <Form.Item label="RX PCN" name="rxPcn" rules={[{ max: 10, message: 'Max 10 characters' }]}>
                    <Input placeholder="optional" disabled={disabled || isUnsupported} style={{ height: 32 }} />
                </Form.Item>
            </Col>
            <Col span={8}>
                <Form.Item label="RX Group" name="rxGroup" rules={[{ max: 15, message: 'Max 15 characters' }]}>
                    <Input placeholder="optional" disabled={disabled || isUnsupported} style={{ height: 32 }} />
                </Form.Item>
            </Col>
        </Row>
    );

    return (
        <Card
            size="small"
            title={cardTitle}
            style={{ borderColor: submitResult ? (submitResult.newStatus === 'Denied' ? '#ffccc7' : '#b7eb8f') : undefined }}
        >
            <Form form={form} layout="vertical" size="middle">
                <Row gutter={12}>
                    <Col span={12}>
                        <Form.Item label="Outcome" name="outcome" rules={[{ required: true, message: 'Required' }]}>
                            <Select placeholder="— select outcome —" disabled={disabled || isUnsupported} allowClear>
                                {OUTCOME_OPTIONS.map(o => (
                                    <Select.Option key={o} value={o}>{o}</Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="Outcome Date" name="outcomeDate" rules={[{ required: true, message: 'Required' }]}>
                            <DatePicker style={{ width: '100%' }} disabled={disabled || isUnsupported} allowClear />
                        </Form.Item>
                    </Col>
                </Row>

                <Collapse
                    ghost
                    size="small"
                    activeKey={activeKeys}
                    onChange={keys => setActiveKeys(typeof keys === 'string' ? [keys] : keys)}
                    items={[{
                        key: 'optional',
                        label: 'Optional fields',
                        children: optionalFields,
                    }]}
                />
            </Form>
        </Card>
    );
});

ServiceCard.displayName = 'ServiceCard';
export default ServiceCard;
